export interface GrievanceDetail {
  grievanceId: string;
  isAnonymous?: boolean;
  fullName?: string;
  email?: string;
  phone?: string;
  address?: string;
  district?: string;
  ministry_name?: string;
  ministry_id?: number;
  office_id?: number;
  subject?: string;
  description?: string;
  category?: string;
  priority?: string;
  status?: string;
  attachments?: (string | GrievanceAttachment)[] | null;
  preferredLanguage?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GrievanceAttachment {
  name: string;
  url?: string;
}

export interface TimelineEntry {
  id?: number;
  status: string;
  message: string;
  office?: string;
  eventDate: string;
}

export const normalizeAttachments = (
  attachments?: (string | GrievanceAttachment)[] | null
): GrievanceAttachment[] => {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((item) =>
    typeof item === 'string' ? { name: item } : { name: item.name, url: item.url }
  );
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const fieldRow = (label: string, value?: string | number | null) => `
  <tr>
    <td style="padding:8px 12px;border:1px solid #e5e7eb;font-weight:600;background:#f9fafb;width:35%;">${label}</td>
    <td style="padding:8px 12px;border:1px solid #e5e7eb;">${value ?? '—'}</td>
  </tr>`;

export const buildGrievanceReportHtml = (
  grievance: GrievanceDetail,
  timeline: TimelineEntry[] = [],
  officeName?: string,
  ministryName?: string
): string => {
  const resolvedMinistry = ministryName || grievance.ministry_name;
  const attachmentItems = normalizeAttachments(grievance.attachments);
  const attachmentsHtml = attachmentItems.length
    ? attachmentItems.map((file) => {
        if (!file.url) return `<li>${file.name}</li>`;
        const absoluteUrl = file.url.startsWith('http')
          ? file.url
          : `${window.location.origin}${file.url.startsWith('/') ? '' : '/'}${file.url}`;
        return `<li><a href="${absoluteUrl}" download="${file.name}">${file.name}</a></li>`;
      }).join('')
    : '<li>None</li>';

  const submitterRows = grievance.isAnonymous
    ? fieldRow('Submission Type', 'Anonymous — personal details not provided')
    : [
        fieldRow('Full Name', grievance.fullName),
        fieldRow('Email', grievance.email),
        fieldRow('Phone', grievance.phone),
        fieldRow('District', grievance.district),
        fieldRow('Address', grievance.address),
      ].join('');

  const timelineRows = timeline.length
    ? timeline.map((item) => `
        <div style="margin-bottom:12px;padding:12px;border:1px solid #e5e7eb;border-radius:6px;">
          <div style="font-size:12px;color:#6b7280;">${formatDate(item.eventDate)}</div>
          <div style="font-weight:600;margin:4px 0;text-transform:capitalize;">${item.status}</div>
          <div style="white-space:pre-wrap;">${item.message}</div>
          ${item.office ? `<div style="font-size:12px;color:#6b7280;margin-top:4px;">${item.office}</div>` : ''}
        </div>`).join('')
    : '<p style="color:#6b7280;">No timeline entries</p>';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Grievance Report - ${grievance.grievanceId}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
    h1 { font-size: 22px; margin-bottom: 4px; }
    h2 { font-size: 16px; margin: 24px 0 8px; color: #374151; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    .header { border-bottom: 2px solid #1d4ed8; padding-bottom: 12px; margin-bottom: 20px; }
    .meta { color: #6b7280; font-size: 13px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Grievance Submission Report</h1>
    <div class="meta">Generated on ${new Date().toLocaleString()}</div>
  </div>

  <h2>Grievance Information</h2>
  <table>
    ${fieldRow('Grievance ID', grievance.grievanceId)}
    ${fieldRow('Status', grievance.status)}
    ${fieldRow('Subject', grievance.subject)}
    ${fieldRow('Category', grievance.category)}
    ${fieldRow('Priority', grievance.priority)}
    ${fieldRow('Submitted On', formatDate(grievance.createdAt))}
  </table>

  <h2>Submitter Details</h2>
  <table>
    ${submitterRows}
  </table>

  <h2>Related Ministry / Office</h2>
  <table>
    ${fieldRow('Ministry', resolvedMinistry)}
    ${fieldRow('Office', officeName || (grievance.office_id ? `Office #${grievance.office_id}` : undefined))}
  </table>

  <h2>Description</h2>
  <div style="padding:12px;border:1px solid #e5e7eb;border-radius:6px;white-space:pre-wrap;">${grievance.description || '—'}</div>

  <h2>Attachments</h2>
  <ul>
    ${attachmentsHtml}
  </ul>

  <h2>Timeline / Status History</h2>
  ${timelineRows}
</body>
</html>`;
};

export const downloadGrievanceReport = async (
  grievance: GrievanceDetail,
  timeline: TimelineEntry[] = [],
  officeName?: string,
  ministryName?: string
): Promise<void> => {
  const { default: jsPDF } = await import('jspdf');
  const { default: html2canvas } = await import('html2canvas');

  // Build a hidden container with the report HTML
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px';
  container.style.background = '#ffffff';
  container.style.padding = '32px';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.color = '#111827';
  container.style.fontSize = '13px';
  container.style.lineHeight = '1.6';
  document.body.appendChild(container);

  // Inject the report body content (no <html>/<head>/<body> wrappers needed here)
  const resolvedMinistry = ministryName || grievance.ministry_name;
  const attachmentItems = normalizeAttachments(grievance.attachments);

  const field = (label: string, value?: string | null) => `
    <div style="display:grid;grid-template-columns:35% 65%;margin-bottom:6px;">
      <span style="font-weight:600;color:#374151;">${label}</span>
      <span style="color:#111827;">${value || '—'}</span>
    </div>`;

  const submitterHtml = grievance.isAnonymous
    ? field('Submission Type', 'Anonymous — personal details not provided')
    : [
        field('Full Name', grievance.fullName),
        field('Email', grievance.email),
        field('Phone', grievance.phone),
        field('District', grievance.district),
        field('Address', grievance.address),
      ].join('');

  const attachmentsHtml = attachmentItems.length
    ? attachmentItems.map((f) => `<div style="padding:4px 0;">📎 ${f.name}</div>`).join('')
    : '<div style="color:#6b7280;">None</div>';

  const timelineHtml = timeline.length
    ? timeline.map((item) => `
        <div style="margin-bottom:10px;padding:10px 14px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;">
          <div style="font-size:11px;color:#6b7280;">${formatDate(item.eventDate)}</div>
          <div style="font-weight:600;text-transform:capitalize;margin:3px 0;">${item.status}</div>
          <div style="white-space:pre-wrap;">${item.message}</div>
          ${item.office ? `<div style="font-size:11px;color:#6b7280;margin-top:3px;">${item.office}</div>` : ''}
        </div>`).join('')
    : '<div style="color:#6b7280;">No timeline entries</div>';

  const section = (title: string, body: string) => `
    <div style="margin-bottom:20px;">
      <h2 style="font-size:14px;font-weight:700;color:#1d4ed8;border-bottom:2px solid #1d4ed8;padding-bottom:5px;margin-bottom:10px;">${title}</h2>
      ${body}
    </div>`;

  container.innerHTML = `
    <div style="border-bottom:3px solid #1d4ed8;padding-bottom:14px;margin-bottom:22px;">
      <h1 style="font-size:20px;font-weight:700;color:#111827;margin:0 0 4px 0;">Grievance Submission Report</h1>
      <div style="font-size:12px;color:#6b7280;">Generated on ${new Date().toLocaleString()}</div>
    </div>

    ${section('Grievance Information', [
      field('Grievance ID', grievance.grievanceId),
      field('Status', grievance.status),
      field('Subject', grievance.subject),
      field('Category', grievance.category),
      field('Priority', grievance.priority),
      field('Submitted On', formatDate(grievance.createdAt)),
    ].join(''))}

    ${section('Submitter Details', submitterHtml)}

    ${section('Related Ministry / Office', [
      field('Ministry', resolvedMinistry),
      field('Office', officeName || (grievance.office_id ? `Office #${grievance.office_id}` : null)),
    ].join(''))}

    ${section('Description', `<div style="padding:10px;border:1px solid #e5e7eb;border-radius:6px;white-space:pre-wrap;background:#f9fafb;">${grievance.description || '—'}</div>`)}

    ${section('Attachments', attachmentsHtml)}

    ${section('Timeline / Status History', timelineHtml)}
  `;

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidthMm = pageWidth;
    const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;

    let yPosition = 0;
    let remainingHeight = imgHeightMm;
    let pageIndex = 0;

    while (remainingHeight > 0) {
      if (pageIndex > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, -yPosition, imgWidthMm, imgHeightMm);
      yPosition += pageHeight;
      remainingHeight -= pageHeight;
      pageIndex++;
    }

    pdf.save(`grievance-${grievance.grievanceId}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};

export const printGrievanceReport = (
  grievance: GrievanceDetail,
  timeline: TimelineEntry[] = [],
  officeName?: string,
  ministryName?: string
) => {
  const html = buildGrievanceReportHtml(grievance, timeline, officeName, ministryName);
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
  };
};
